import { inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandlerFn,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, from, of, switchMap, tap, catchError, throwError, timeout } from 'rxjs';
import { CacheStore } from './cache-store.service';
import { MutationQueue } from './mutation-queue.service';
import { NetworkService } from './network.service';
import { AuthService } from '../../services/auth.service';

/**
 * Intercepteur HTTP qui rend Dinki utilisable même sans connexion.
 *
 * <p>Stratégie :</p>
 * <ul>
 *   <li><b>GET online</b> → requête normale + mise en cache de la réponse (best-effort).</li>
 *   <li><b>GET offline</b> → sert la dernière entrée connue en cache (même expirée),
 *       avec un header {@code X-From-Cache} pour que l'UI puisse afficher un badge.</li>
 *   <li><b>POST/PUT/PATCH/DELETE online</b> → requête normale, le retour invalide
 *       le cache du type concerné.</li>
 *   <li><b>POST/PUT/PATCH/DELETE offline</b> → mis en queue (persisté IDB), retourne
 *       immédiatement une réponse synthétique 202 (Accepted) compatible avec le
 *       format {@code ApiResponse<T>} attendu par les services frontend, avec
 *       {@code data} = body envoyé par le client (le mieux qu'on puisse faire offline).</li>
 *   <li>En cas d'échec réseau pendant une requête online, l'intercepteur bascule
 *       automatiquement la mécanique offline (markOffline + cache fallback ou queue).</li>
 * </ul>
 *
 * <p>Sont bypassées :</p>
 * <ul>
 *   <li>les requêtes portant {@code X-Offline-Skip: 1} (ping santé, replay de mutations)</li>
 *   <li>les requêtes d'authentification ({@code /api/v*\/x-auth/*}) : il faut un vrai
 *       contrôle online/offline ici, pas une queue.</li>
 *   <li>les uploads multipart (fichiers, avatars) : impossible de re-sérialiser
 *       fidèlement un FormData en queue, et le timeout court d'écriture casserait
 *       les vrais uploads de fichiers.</li>
 * </ul>
 */
export const offlineInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const cache = inject(CacheStore);
  const queue = inject(MutationQueue);
  const network = inject(NetworkService);
  const auth = inject(AuthService);

  // ─── Bypasses ─────────────────────────────────────────────────────────
  if (req.headers.get('X-Offline-Skip') === '1') return next(req);
  if (req.headers.get('X-Offline-Replay') === '1') return next(req);
  if (!isApiCall(req)) return next(req);
  if (isAuthCall(req)) return next(req);
  if (isMultipart(req)) return next(req);
  // Les admins Dinki travaillent toujours en ligne — pas de cache local, pas
  // de queue de mutations. Ils opèrent sur des données plateforme (plans,
  // ateliers, abonnements) qui doivent rester fraîches et cohérentes.
  if (auth.hasRole('ADMIN')) return next(req);

  const method = req.method.toUpperCase();
  const type = CacheStore.typeFromUrl(req.url);

  // ─── GET ─────────────────────────────────────────────────────────────
  if (method === 'GET') {
    const cacheKey = `GET:${req.urlWithParams}`;
    if (!network.isOnline()) {
      return from(cache.getStale<unknown>(cacheKey)).pipe(
        switchMap((cached) => {
          if (!cached) {
            return throwError(() => makeOfflineError(req.url));
          }
          return of(
            new HttpResponse({
              status: 200,
              statusText: 'OK (from cache)',
              url: req.url,
              body: cached.payload,
              headers: new HttpHeaders({
                'X-From-Cache': '1',
                'X-Cache-Updated-At': String(cached.updatedAt),
              }),
            }),
          );
        }),
      );
    }
    // Online : on laisse passer + on met en cache la réponse
    return next(req).pipe(
      timeout(20_000),
      tap((event) => {
        if (event instanceof HttpResponse && event.status >= 200 && event.status < 300) {
          void cache.put(cacheKey, type, event.body);
        }
      }),
      catchError((err) => {
        if (isNetworkError(err)) {
          network.markOffline();
          // Fallback cache si possible
          return from(cache.getStale<unknown>(cacheKey)).pipe(
            switchMap((cached) => {
              if (!cached) return throwError(() => err);
              return of(
                new HttpResponse({
                  status: 200,
                  statusText: 'OK (from cache after network error)',
                  url: req.url,
                  body: cached.payload,
                  headers: new HttpHeaders({ 'X-From-Cache': '1' }),
                }),
              );
            }),
          );
        }
        return throwError(() => err);
      }),
    );
  }

  // ─── POST / PUT / PATCH / DELETE ─────────────────────────────────────
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    if (!network.isOnline()) {
      return from(enqueue(req, queue, type)).pipe(
        switchMap((mutId) => of(makeQueuedResponse(req, mutId, false))),
      );
    }
    // Online : on laisse passer + invalidation cache du type concerné
    return next(req).pipe(
      timeout(30_000),
      tap((event) => {
        if (event instanceof HttpResponse && event.status >= 200 && event.status < 300) {
          void cache.invalidateByType(type);
        }
      }),
      catchError((err) => {
        if (isNetworkError(err)) {
          network.markOffline();
          return from(enqueue(req, queue, type)).pipe(
            switchMap((mutId) => of(makeQueuedResponse(req, mutId, true))),
          );
        }
        return throwError(() => err);
      }),
    );
  }

  // Autres méthodes (HEAD, OPTIONS) : passe normalement
  return next(req);
};

// ─── helpers de détection ────────────────────────────────────────────────

function isApiCall(req: HttpRequest<unknown>): boolean {
  return /\/api\/v\d+\//.test(req.url);
}

/**
 * Endpoints d'auth : login/register/forgot/reset.
 *
 * <p>On ne met PAS en queue les tentatives de login : un utilisateur qui essaie
 * de se connecter offline doit voir une erreur immédiate plutôt qu'une fausse
 * promesse de connexion différée.</p>
 */
function isAuthCall(req: HttpRequest<unknown>): boolean {
  return /\/api\/v\d+\/x-auth\//.test(req.url)
      || /\/api\/v\d+\/auth\//.test(req.url);
}

/**
 * Une requête multipart (upload de fichier — avatar, image modèle, scan reçu)
 * ne peut pas être re-sérialisée fidèlement depuis IndexedDB (FormData/Blob).
 * On la laisse passer telle quelle et on tolère l'échec si le réseau est down.
 */
function isMultipart(req: HttpRequest<unknown>): boolean {
  const ct = req.headers.get('Content-Type') || '';
  if (ct.includes('multipart/form-data')) return true;
  // FormData explicite (Angular ne set pas toujours le Content-Type)
  if (typeof FormData !== 'undefined' && req.body instanceof FormData) return true;
  return false;
}

function isNetworkError(err: any): boolean {
  if (!err) return false;
  if (err.status === 0) return true;
  if (err.name === 'TimeoutError') return true;
  const msg = String(err.message || err.error?.message || '');
  return /timeout|network|failed to fetch|fetch failed/i.test(msg);
}

// ─── helpers de construction de réponse ──────────────────────────────────

/**
 * Erreur structurée pour les GET offline sans cache disponible.
 * Reconnue par {@code notification.service.extractMessage} pour un message
 * utilisateur ad hoc.
 */
function makeOfflineError(url: string): any {
  return {
    status: 0,
    statusText: 'Offline',
    message: `Hors ligne — aucune donnée locale pour ${url}.`,
    name: 'OfflineError',
    error: { offline: true, url },
  };
}

/**
 * Construit la réponse synthétique 202 retournée quand une écriture est mise
 * en queue. Le format mime celui d'un {@code ApiResponse<T>} backend pour que
 * les services existants (qui font {@code pipe(map(r => r.data))}) continuent
 * à fonctionner. {@code data} est une copie du body envoyé, enrichie d'un
 * drapeau {@code _offlineQueued} pour que les composants curieux puissent
 * adapter leur UI.
 *
 * @param req      requête originale
 * @param mutId    id de la mutation en queue
 * @param onlineToOffline true si on était online avant l'envoi (= perte réseau
 *                 en cours de requête), false si on était déjà offline.
 */
function makeQueuedResponse(
  req: HttpRequest<unknown>,
  mutId: number,
  onlineToOffline: boolean,
): HttpResponse<any> {
  const payload = synthesizeDataPayload(req, mutId);
  const body = {
    success: true,
    queued: true,
    mutationId: mutId,
    message: onlineToOffline
      ? 'Réseau perdu — action enregistrée localement, sera synchronisée au retour.'
      : 'Action enregistrée localement, sera synchronisée au retour du réseau.',
    data: payload,
  };
  return new HttpResponse({
    status: 202,
    statusText: 'Accepted — queued offline',
    url: req.url,
    body,
    headers: new HttpHeaders({
      'X-Offline-Queued': String(mutId),
      'X-Offline-Source': onlineToOffline ? 'online-fallback' : 'offline',
    }),
  });
}

/**
 * Construit le {@code data} synthétique. Pour POST/PUT/PATCH : on retourne le
 * body envoyé enrichi d'un id local. Pour DELETE : null. Les composants qui
 * lisent {@code .reference} ou {@code .id} sur la réponse n'auront pas de
 * crash (juste un placeholder local).
 */
function synthesizeDataPayload(req: HttpRequest<unknown>, mutId: number): any {
  if (req.method.toUpperCase() === 'DELETE') return null;
  const localRef = `offline-${mutId}-${Date.now()}`;
  if (req.body && typeof req.body === 'object') {
    return {
      ...(req.body as Record<string, unknown>),
      reference: (req.body as any).reference ?? localRef,
      _offlineQueued: true,
      _offlineMutationId: mutId,
    };
  }
  return { reference: localRef, _offlineQueued: true, _offlineMutationId: mutId };
}

// ─── enqueue ─────────────────────────────────────────────────────────────

async function enqueue(
  req: HttpRequest<unknown>,
  queue: MutationQueue,
  type: string,
): Promise<number> {
  const headers: Record<string, string> = {};
  req.headers.keys().forEach((k) => {
    const lower = k.toLowerCase();
    // Authorization sera réinjecté par jwtInterceptor au moment du replay.
    if (lower === 'authorization') return;
    // Ne pas stocker les marqueurs internes
    if (lower === 'x-offline-skip' || lower === 'x-offline-replay') return;
    const v = req.headers.get(k);
    if (v != null) headers[k] = v;
  });

  return queue.enqueue({
    method: req.method as any,
    url: req.urlWithParams,
    body: req.body,
    headers,
    type,
    label: humanLabel(req, type),
  });
}

function humanLabel(req: HttpRequest<unknown>, type: string): string {
  const m = req.method.toUpperCase();
  const labelMap: Record<string, string> = {
    POST: 'Création',
    PUT: 'Modification',
    PATCH: 'Mise à jour',
    DELETE: 'Suppression',
  };
  // Détecte les actions spéciales (suspend, toggle, etc.)
  const action = req.url.match(/\/(suspend|activate|block|toggle|cancel|refund|recompute|reset)(\?|$)/);
  if (action) {
    return `${labelMap[m] || m} ${type} — ${action[1]}`;
  }
  return `${labelMap[m] || m} ${type}`;
}
